import { CoralogixRum } from '@coralogix/browser'
import { User } from 'oidc-client-ts'
import { useEffect } from 'react'
import { useAuth } from 'react-oidc-context'
import { Environments, getEnv } from 'utils/env'
import { getVersion } from 'utils/run-cache-buster'

export const Coralogix = () => {
	const { user } = useAuth()

	useEffect(() => {
		const initCoralogix = async () => {
			if (CoralogixRum.isInited) {
				return
			}

			try {
				const coralogixApiKey = getCoralogixApiKeyForEnv(getEnv() as Environments)

				if (!coralogixApiKey) {
					// eslint-disable-next-line no-console
					console.warn('* coralogix api key not found for environment:', getEnv())

					return
				}

				CoralogixRum.init({
					public_key: coralogixApiKey,
					application: 'AdminUI',
					version: await getVersion(),
					coralogixDomain: 'US2',
					sessionConfig: {
						keepSessionAfterReload: true,
					},
					sessionRecordingConfig: {
						// https://coralogix.com/docs/user-guides/rum/product-features/session-replay/web/#configuration-options
						enable: true,
						autoStartSessionRecording: true,
						sessionRecordingSampleRate: 100,
					},
					traceParentInHeader: {
						enabled: true,
						options: {
							propagateTraceHeaderCorsUrls: [
								new RegExp('https://[^.]+\\.(dev\\.|int\\.|test\\.|local\\.|)corestream\\.com'),
							],
						},
					},
				})
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('* coralogix init error:', error)
			}
		}

		initCoralogix()
	}, [])

	useEffect(() => {
		const setUserContext = async (user: User) => {
			try {
				CoralogixRum.setUserContext({
					user_id: user.profile.sub,
					user_name: user.profile.name || '',
					user_email: user.profile.email || '',
					user_metadata: {
						role: user.profile.role,
					},
				})
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('* coralogix setUserContext error:', error)
			}
		}

		if (user) {
			setUserContext(user)
		}
	}, [user])

	return null
}

/**
 * From the environment, get the correct Coralogix API key configured in the .env files
 */
// TODO: Set the vite env variables for other environements
export function getCoralogixApiKeyForEnv(env: Environments): string {
	const coralogixApiKeys = {
		[Environments.DEV]: import.meta.env.VITE_CORALOGIX_API_KEY_DEV,
		[Environments.INT]: import.meta.env.VITE_CORALOGIX_API_KEY_INT,
		[Environments.UAT]: import.meta.env.VITE_CORALOGIX_API_KEY_UAT,
		[Environments.PROD]: import.meta.env.VITE_CORALOGIX_API_KEY_PROD,
	}

	const coralogixApiKey = coralogixApiKeys[env] || ''

	return coralogixApiKey
}
